import { useEffect, useState, useCallback } from 'react';
import { Download, X, Smartphone } from 'lucide-react';

const DISMISS_KEY = 'pwa_install_prompt_dismissed_at';
const DISMISS_WINDOW_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

/**
 * PWA install prompt — listens for the `beforeinstallprompt` event, shows a
 * friendly bottom-sheet on supported browsers (Chrome/Edge/Android). Dismissing
 * snoozes the prompt for 14 days. Renders nothing on unsupported browsers or
 * when the app is already installed (`display-mode: standalone`).
 */
export default function PWAInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    // Hide if already installed
    try {
      if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) return undefined;
    } catch {
      /* noop */
    }
    // Hide if recently dismissed
    try {
      const last = Number(localStorage.getItem(DISMISS_KEY) || 0);
      if (last && Date.now() - last < DISMISS_WINDOW_MS) return undefined;
    } catch {
      /* noop */
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    const installedHandler = () => { setVisible(false); setDeferred(null); };
    window.addEventListener('appinstalled', installedHandler);
    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, []);

  const onInstall = useCallback(async () => {
    if (!deferred) return;
    setInstalling(true);
    try {
      deferred.prompt();
      await deferred.userChoice;
    } catch {
      /* user dismissed */
    } finally {
      setDeferred(null);
      setVisible(false);
      setInstalling(false);
    }
  }, [deferred]);

  const onDismiss = useCallback(() => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* noop */ }
    setVisible(false);
  }, []);

  if (!visible) return null;

  return (
    <div
      data-testid="pwa-install-prompt"
      className="fixed bottom-24 right-6 z-40 w-80 rounded-2xl border border-sky-500/25 shadow-2xl shadow-black/40 overflow-hidden"
      style={{ background: 'hsl(222 47% 10% / 0.98)', backdropFilter: 'blur(20px)', animation: 'pwaIn .35s cubic-bezier(.16,1,.3,1)' }}
    >
      <div className="px-4 py-4 flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-sky-500/15 border border-sky-500/30 flex items-center justify-center flex-shrink-0">
          <Smartphone className="w-5 h-5 text-sky-300" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-semibold text-foreground">Install Sager Aviation</h4>
          <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">
            Add to your home screen for one-tap access. Request flights, sign logs and track hours like a native app.
          </p>
        </div>
        <button
          data-testid="pwa-dismiss-btn"
          onClick={onDismiss}
          className="p-1 rounded-lg hover:bg-white/5 text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
          aria-label="Dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="px-4 pb-4 flex items-center gap-2">
        <button
          data-testid="pwa-install-btn"
          onClick={onInstall}
          disabled={installing}
          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-sky-500 hover:bg-sky-400 text-white text-xs font-semibold transition-colors disabled:opacity-60"
        >
          <Download className="w-3.5 h-3.5" />
          {installing ? 'Installing…' : 'Install App'}
        </button>
        <button
          data-testid="pwa-later-btn"
          onClick={onDismiss}
          className="px-3 py-2 rounded-lg text-xs text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors"
        >
          Later
        </button>
      </div>
      <style>{`@keyframes pwaIn{from{opacity:0;transform:translateY(16px) scale(.96)}to{opacity:1;transform:translateY(0) scale(1)}}`}</style>
    </div>
  );
}
